import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {MultiSelect} from 'primereact/multiselect'
import React, {forwardRef, useEffect, useImperativeHandle, useMemo} from 'react'
import {v4 as uuidv4} from 'uuid'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {ReactComponent as MdSupportAgent} from '../../assets/icons/MdSupportAgent.svg'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {useListSupportTeams} from '../support-teams/query-keys/SupportTeamKeysManager'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {useAssignPatient} from '../user-property/query-keys/UserPropertyQueryManager'
import {useDatatableContext} from './DataTableContext'
import {
    assignmentEvent,
    assignmentEventTypes,
    getAssignmentEventKey,
    removeToggleAssignmentFormListener,
} from './broadcast/AssignmentEvent'
import {usePatientContext} from './information/StyleDetailInformationWrapper'
import {
    getSupportTeamEventKey,
    onSupportTeamTableChangeRemoveListener,
    onUserSupportTeamDetailChangeRemoveListener,
    supportTeamEvent,
    supportTeamEventTypes,
} from './support-team/broadcast/SupportTeamEvent'

const InitialOwnedSupportTeamFilterRequest = {}
const StyledAssignmentSupportTeam = forwardRef(({userIds = []}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {currentViewedUser} = usePatientContext()
    const {tableType} = useDatatableContext()
    //#endregion
    //#region Translate
    const {
        translateKeys,
        translateCommons,
        translateDatatable,
        translateOptions,
    } = TranslateUtils()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const panelVisible = useSignal(false)
    const selectedItems = useSignal([])
    const handleChangeSelectedItems = e => {
        selectedItems.value = e.value
    }
    //#endregion
    const {data: supportTeamOptions, isFetched} = useListSupportTeams(
        currentViewedUser?.hospitalId,
        user?.id,
        true,
        {
            hospitalId: currentViewedUser?.hospitalId,
        },
        !!user?.id,
    )
    const {data: ownedSupportTeams, refetch} = useListSupportTeams(
        currentViewedUser?.hospitalId,
        user?.id,
        false,
        {
            ...InitialOwnedSupportTeamFilterRequest,
            userIdInParticipant: currentViewedUser?.id,
            hospitalId: currentViewedUser?.hospitalId,
        },
        !!currentViewedUser?.id && !!user?.id,
    )

    //#region Memo
    const ownSupportTeamIds = useMemo(() => {
        if (
            !ownedSupportTeams ||
            ownedSupportTeams?.length < 1 ||
            (!!userIds && userIds.length > 1)
        ) {
            return []
        }

        return ownedSupportTeams?.map(x => x.id)
    }, [ownedSupportTeams, userIds])
    //#endregion

    //#region Component State
    const handleShowForm = () => {
        blockOverflownScroll()
        visible.value = true

        if (ownSupportTeamIds?.length > 0 && userIds?.length === 1) {
            selectedItems.value = [...ownSupportTeamIds]
        }
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        visible.value = false
        selectedItems.value = []
    }

    const queryClient = useQueryClient()
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        user?.id,
    )

    const userSupportTeamQueryKeys = useMemo(() => {
        return userPropertyKeys.pattern({
            type: UserPropertyType.userSupportTeam,
        })
    }, [userPropertyKeys])

    const {mutateAsync: assignPatientToSupportTeam} = useAssignPatient(
        UserPropertyType.userSupportTeam,
        user?.id,
    )

    const handleAssignSupportTeam = async value => {
        let toPostData = {
            userIds: userIds,
            supportTeamIds: selectedItems.value,
            userId: currentViewedUser?.id ? currentViewedUser?.id : null,
            hospitalId: currentViewedUser?.hospitalId,
        }

        const responseStatus = await assignPatientToSupportTeam(toPostData)
        if (responseStatus && responseStatus === HTTP_STATUS_OK) {
            const {supportTeamKeys} = QueryKeyManager(
                QueryKeyTypes.supportTeam,
                user?.id,
            )
            queryClient.invalidateQueries(userSupportTeamQueryKeys)
            queryClient.invalidateQueries(supportTeamKeys.pattern())

            if (userIds?.length === 1) {
                refetch()
            }

            handleHideForm()
        }
    }
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // USER SUPPORT TEAM CHANGE
        supportTeamEvent.on(
            getSupportTeamEventKey(
                supportTeamEventTypes.USER_SUPPORT_TEAM_CHANGE,
            ),
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (!data?.hospitalId) {
                    return
                }

                if (userRole === SystemRole.USER_WITH_ROLE) {
                    if (data?.hospitalId !== currentHospital?.id) {
                        return
                    }
                } else {
                    if (data?.hospitalId !== currentViewedUser?.hospitalId) {
                        return
                    }
                }

                if (currentViewedUser?.id !== data?.userId) {
                    return
                }

                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: supportTeamKeys.pattern(),
                })
            },
        )

        // TABLE
        supportTeamEvent.on(
            getSupportTeamEventKey(supportTeamEventTypes.TABLE_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                if (
                    !data?.participantIds ||
                    !data?.participantIds?.includes(user?.id)
                ) {
                    return
                }

                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: supportTeamKeys.pattern(),
                })
            },
        )

        return () => {
            onSupportTeamTableChangeRemoveListener()
            onUserSupportTeamDetailChangeRemoveListener()
        }
    }, [])
    //#endregion

    //#region Render & Template
    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }
    //#endregion

    //#region Event Lister for Toggle
    useEffect(() => {
        assignmentEvent.on(
            `${getAssignmentEventKey(
                tableType,
                assignmentEventTypes.ON_TOGGLE_SUPPORT_TEAM,
            )}`,
            () => {
                handleShowForm()
            },
        )

        return () => {
            removeToggleAssignmentFormListener(
                tableType,
                assignmentEventTypes.ON_TOGGLE_SUPPORT_TEAM,
            )
        }
    }, [])
    //#endregion
    return (
        <Dialog
            header={
                <h5 className="mb-0 font-bold">
                    <strong>{translateKeys('Assign Support Team')}</strong>
                </h5>
            }
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll={true}
            style={{width: '50vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}>
            <div className="w-full">
                <div className="mb-2">
                    <h4 className="font-bold mb-1 underline-60">
                        {translateCommons('Support Team')}
                    </h4>
                </div>
                <p>
                    Select one or more Support Team to assign the selected
                    patients.
                </p>

                <div className="col-12 mb-3">
                    <div className="flex align-items-center justify-content-between py-2">
                        <h6 className="font-bold">
                            {translateDatatable('Support Team')}
                        </h6>
                    </div>
                    <MultiSelect
                        className="w-full h-3rem p-inputtext p-0 font-bold"
                        options={
                            isArrayAndNotEmpty(supportTeamOptions)
                                ? translateOptions(supportTeamOptions)
                                : []
                        }
                        placeholder={translateCommons('Select Support Team')}
                        defaultValue={[]}
                        maxSelectedLabels={4}
                        display={
                            selectedItems.value?.length < 5 ? 'chip' : 'comma'
                        }
                        value={selectedItems.value || []}
                        onChange={handleChangeSelectedItems}
                        itemTemplate={item => {
                            return (
                                <div className="flex align-items-center hover:text-primary">
                                    <div className="mx-2 mr-2">
                                        {item?.extraData && (
                                            <img
                                                src={item?.extraData}
                                                alt={item.label}
                                                width={22}
                                                height={22}
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <h6 className="font-bold m-0 mb-1">
                                            {item.label}
                                        </h6>
                                        <small>{item?.data}</small>
                                    </div>
                                </div>
                            )
                        }}
                        panelFooterTemplate={() =>
                            renderPanelFooterTemplate(selectedItems.value)
                        }
                        {...panelVisiblilityProps}
                    />
                    <p className="font-bold">
                        You have selected{' '}
                        <span className="text-darkcyan">
                            {selectedItems.value?.length < 1
                                ? 'no'
                                : selectedItems.value?.length}{' '}
                            Support Team
                        </span>{' '}
                        out of{' '}
                        <span className="text-darkcyan">
                            {supportTeamOptions?.length} available Support Team
                        </span>
                    </p>

                    <Divider align="center" layout="horizontal" />
                    <h6 className="font-bold mb-2">
                        {translateCommons('Note')}
                    </h6>
                    <ul>
                        <li className="mb-2">
                            You can assign the selected patients one or more
                            Support Team
                        </li>
                        <li className="mb-2">
                            If you want to remove the patient from the Support
                            Team, please select only that patient and change the
                            Support Team Assignment.
                        </li>
                        <li className="mb-2">
                            By assigning a support team, you are allowing the
                            selected team to directly communicate with the
                            patient.
                        </li>
                    </ul>

                    {selectedItems.value?.length > 0 && (
                        <React.Fragment>
                            <Divider align="center" layout="horizontal" />
                            <h6 className="font-bold">
                                {translateCommons('Summary')}
                            </h6>
                            <p>
                                You are about to assign the selected patients to
                                the following Support Team:
                            </p>
                            <ul>
                                {selectedItems.value?.length > 0 ? (
                                    <React.Fragment>
                                        {selectedItems.value?.map(
                                            (selectedTeam, index) => {
                                                if (index > 3) {
                                                    return null
                                                }

                                                const spTeam =
                                                    supportTeamOptions?.find(
                                                        x =>
                                                            x.value ===
                                                            selectedTeam,
                                                    )

                                                if (!spTeam) {
                                                    return null
                                                }

                                                return (
                                                    <li
                                                        key={`${uuidv4()}`}
                                                        className="mb-2">
                                                        <div className="flex align-items-center hover:text-primary">
                                                            <div className="mx-2">
                                                                {spTeam?.label && (
                                                                    <MdSupportAgent
                                                                        width={
                                                                            20
                                                                        }
                                                                        height={
                                                                            20
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="font-bold">
                                                                {spTeam.label}
                                                            </div>
                                                        </div>
                                                    </li>
                                                )
                                            },
                                        )}

                                        {selectedItems.value?.length > 4 ? (
                                            <li className="mb-2 py-2">
                                                <span className="font-bold">
                                                    +{' '}
                                                    {selectedItems.value
                                                        ?.length - 4}{' '}
                                                    more Support Team
                                                </span>
                                            </li>
                                        ) : null}
                                    </React.Fragment>
                                ) : null}
                            </ul>
                        </React.Fragment>
                    )}
                </div>
                <Button
                    label={translateCommons('SUBMIT')}
                    icon="pi pi-send"
                    onClick={handleAssignSupportTeam}
                    autoFocus
                    className="col-12 h-3rem mb-3"
                />
            </div>
            {/* <CustomForm
                onSubmit={handleAssignSupportTeam}
                layout={formLayout.inline}
                dataList={[
                    {
                        key: patientKeys.supportTeam,
                        component: component.multiSelect,
                        options: supportTeamOptions,
                        placeholder: translateCommons('Select Support Team'),
                        defaultValue: ownedSupportTeams?.length
                            ? ownedSupportTeams?.map(item => item?.value)
                            : [],
                        additionalInfo: translateCommons(
                            'By assigning a support team, you are allowing the selected team to directly communicate with the patient.',
                        ),
                    },
                ]}
            /> */}
        </Dialog>
    )
})

export default StyledAssignmentSupportTeam
